import axios from 'axios'
import { useUserStore } from '@/stores/user'
import { useAppStatusStore } from '@/stores/appStatus'
import router from '@/router'
import { getDeviceId } from '@/utils/deviceId'

/** API base URL: env at build time, or same-origin in prod (Vite inlines localhost if used as `||` fallback). */
export function resolveApiBaseUrl(): string {
  if (import.meta.env.VITE_API_URL) {
    return import.meta.env.VITE_API_URL
  }
  if (typeof window !== 'undefined') {
    const { hostname, origin } = window.location
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return 'http://localhost:8000/api/v1'
    }
    return `${origin}/api/v1`
  }
  return '/api/v1'
}

const apiClient = axios.create({
  baseURL: resolveApiBaseUrl(),
  headers: {
    'Content-Type': 'application/json',
  },
})

apiClient.interceptors.request.use(
  (config) => {
    const userStore = useUserStore()
    if (!(config as any)._skipAuthRefresh && userStore.token) {
      config.headers['Authorization'] = `Bearer ${userStore.token}`
    }
    config.headers['X-Device-Id'] = getDeviceId()
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    // Backend truly unreachable: no response (network), or non-JSON proxy 502/503/504.
    // JSON 502/503 from our API usually means a payment provider is down — keep the app usable.
    const status = error.response?.status;
    const isNetworkError = !error.response && error.code !== 'ERR_CANCELED';
    const isGatewayError = status === 502 || status === 503 || status === 504;
    const contentType = String(error.response?.headers?.['content-type'] || '');
    const isJsonApiError = contentType.includes('application/json');
    if (isNetworkError || (isGatewayError && !isJsonApiError)) {
      useAppStatusStore().markBackendUnavailable();
      return Promise.reject(error);
    }

    const originalRequest = error.config;
    if (error.response?.status === 401 && originalRequest && !originalRequest._retry && !originalRequest._skipAuthRefresh) {
      originalRequest._retry = true;
      const userStore = useUserStore();
      const refreshed = await userStore.refreshToken();
      if (refreshed === 'offline') return Promise.reject(error);
      if (refreshed === true && userStore.token) {
        originalRequest.headers['Authorization'] = 'Bearer ' + userStore.token;
        return apiClient(originalRequest);
      }
      userStore.logout(true);
      router.push('/login');
      return Promise.reject(error);
    }
    return Promise.reject(error);
  }
)

export default apiClient

// Типы для использования в других модулях
export interface ApiResponse<T = any> {
  data: T
  message?: string
  status: string
}

export interface PaginatedResponse<T = any> {
  items: T[]
  total: number
  page: number
  size: number
  pages: number
}

export interface ApiError {
  message: string
  status?: number
  data?: any
} 